import {
  useCallback,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Drawer from '@material-ui/core/Drawer';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import { Button } from './Button';
import { TONE_COLORS, ON_TONE } from './status';

interface FormShellProps {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  onSubmit: () => void | Promise<void>;
  submitLabel?: ReactNode;
  cancelLabel?: string;
  submitting?: boolean;
  submitDisabled?: boolean;
  /** Destructive action: red submit button. */
  danger?: boolean;
  /** Message of the last failed submission. */
  error?: string | null;
  /**
   * Replaces the submit button: a block at the bottom of the form that holds its own
   * `type="submit"` buttons (e.g. a warning with « Lancer quand même »). Cancel stays.
   */
  footer?: ReactNode;
  children: ReactNode;
}

function FormBody({
  onClose,
  onSubmit,
  submitLabel = 'Enregistrer',
  cancelLabel = 'Annuler',
  submitting,
  submitDisabled,
  danger,
  error,
  footer,
  children,
  layout,
}: Omit<FormShellProps, 'open' | 'title'> & { layout: 'dialog' | 'drawer' }) {
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!submitting && !submitDisabled) onSubmit();
  };
  const actions = (
    <>
      <Button onClick={onClose} disabled={submitting}>
        {cancelLabel}
      </Button>
      {!footer && (
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={submitting || submitDisabled}
          style={
            danger
              ? { backgroundColor: TONE_COLORS.error, color: ON_TONE }
              : undefined
          }
          startIcon={submitting ? <CircularProgress size={14} /> : undefined}
        >
          {submitLabel}
        </Button>
      )}
    </>
  );
  const errorNode = error && (
    <Typography
      role="alert"
      variant="body2"
      style={{
        color: TONE_COLORS.error,
        marginTop: 8,
        wordBreak: 'break-word',
      }}
    >
      {error}
    </Typography>
  );
  if (layout === 'dialog') {
    return (
      <form onSubmit={submit}>
        <DialogContent>
          {children}
          {errorNode}
          {footer}
        </DialogContent>
        <DialogActions>{actions}</DialogActions>
      </form>
    );
  }
  return (
    <form
      onSubmit={submit}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '8px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {children}
        {errorNode}
      </div>
      {footer && <div style={{ padding: '0 24px' }}>{footer}</div>}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          padding: 16,
        }}
      >
        {actions}
      </div>
    </form>
  );
}

/** Modal form: title, fields, error line, cancel/submit. Enter submits. */
export function FormDialog({
  open,
  title,
  onClose,
  maxWidth = 'sm',
  ...body
}: FormShellProps & { maxWidth?: 'xs' | 'sm' | 'md' }) {
  return (
    <Dialog
      open={open}
      onClose={body.submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{title}</DialogTitle>
      <FormBody {...body} onClose={onClose} layout="dialog" />
    </Dialog>
  );
}

/** Side form for longer flows; never wider than the screen. */
export function FormDrawer({
  open,
  title,
  onClose,
  width = 480,
  ...body
}: FormShellProps & { width?: number }) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={body.submitting ? undefined : onClose}
      PaperProps={{ style: { width: `min(${width}px, 100vw)` } }}
    >
      <Typography variant="h6" style={{ padding: '16px 24px 8px' }}>
        {title}
      </Typography>
      <FormBody {...body} onClose={onClose} layout="drawer" />
    </Drawer>
  );
}

export interface ConfirmOptions {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirmer',
  cancelLabel = 'Annuler',
  danger,
  onConfirm,
  onCancel,
}: ConfirmOptions & {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{cancelLabel}</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onConfirm}
          style={
            danger
              ? { backgroundColor: TONE_COLORS.error, color: ON_TONE }
              : undefined
          }
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Replaces `window.confirm`: `const { confirm, dialog } = useConfirm()`, render `{dialog}`,
 * then `if (!(await confirm({...}))) return;`.
 */
export function useConfirm() {
  const [options, setOptions] = useState<ConfirmOptions>();
  const resolver = useRef<(ok: boolean) => void>();

  const confirm = useCallback(
    (next: ConfirmOptions) =>
      new Promise<boolean>(resolve => {
        resolver.current = resolve;
        setOptions(next);
      }),
    [],
  );
  const settle = (ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = undefined;
    setOptions(undefined);
  };

  const dialog = (
    <ConfirmDialog
      open={Boolean(options)}
      title={options?.title ?? ''}
      message={options?.message}
      confirmLabel={options?.confirmLabel}
      cancelLabel={options?.cancelLabel}
      danger={options?.danger}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );
  return { confirm, dialog };
}
